import { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: ''
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await axios.post(`${API}/contact`, formData);
            toast.success('Message sent! We\'ll get back to you soon.');
            setFormData({ name: '', email: '', phone: '', message: '' });
        } catch (error) {
            toast.error('Failed to send message. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#F8F9FA] pb-8" data-testid="contact-page">
            <div className="container-main py-8 md:py-12">
                <div className="max-w-4xl mx-auto">
                    {/* Header */}
                    <div className="text-center mb-12">
                        <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                            Get in Touch
                        </h1>
                        <p className="text-lg text-slate-600 max-w-xl mx-auto">
                            Have a question about your order or want to set up a wholesale account? 
                            We'd love to hear from you.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Contact Info */}
                        <div className="space-y-6">
                            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
                                <h2 className="text-xl font-bold text-slate-900 mb-6">Contact Information</h2>
                                
                                <div className="space-y-4">
                                    <div className="flex items-start gap-4">
                                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                                            <MapPin className="w-5 h-5 text-[#1B4D3E]" />
                                        </div>
                                        <div>
                                            <h3 className="font-medium text-slate-900">Pickup Location</h3>
                                            <p className="text-slate-600">Orange Grove Market</p>
                                            <p className="text-slate-600">Lilyfield NSW 2040</p>
                                            <p className="text-slate-500 text-sm">Saturdays, 7:00 AM – 1:00 PM</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-4">
                                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                                            <Phone className="w-5 h-5 text-[#1B4D3E]" />
                                        </div>
                                        <div>
                                            <h3 className="font-medium text-slate-900">Phone</h3>
                                            <p className="text-slate-600">0414 829 929</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-4">
                                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                                            <Mail className="w-5 h-5 text-[#1B4D3E]" />
                                        </div>
                                        <div>
                                            <h3 className="font-medium text-slate-900">Email</h3>
                                            <p className="text-slate-600">henrys3@bigpond.com</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-4">
                                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                                            <Clock className="w-5 h-5 text-[#1B4D3E]" />
                                        </div>
                                        <div>
                                            <h3 className="font-medium text-slate-900">Order Schedule (Sydney time)</h3>
                                            <p className="text-slate-600"><span className="font-medium">Delivery</span> — cut-off Saturday 12 PM, delivered Tuesday the following week</p>
                                            <p className="text-slate-600"><span className="font-medium">Pickup</span> — cut-off Thursday 12 PM, ready Saturday 7 AM–1 PM at Orange Grove Market</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Pickup location */}
                            <div className="bg-slate-100 rounded-xl h-48 flex flex-col items-center justify-center text-center p-4">
                                <span className="text-slate-800 font-semibold">Orange Grove Market</span>
                                <span className="text-slate-600">Lilyfield NSW 2040</span>
                                <span className="text-slate-500 text-sm mt-1">Pickup: Saturdays, 7:00 AM – 1:00 PM</span>
                            </div>
                        </div>

                        {/* Contact Form */}
                        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
                            <h2 className="text-xl font-bold text-slate-900 mb-6">Send us a Message</h2>
                            
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <Label htmlFor="name">Your Name *</Label>
                                    <Input
                                        id="name"
                                        value={formData.name}
                                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                        placeholder="John Doe"
                                        className="mt-1"
                                        required
                                        data-testid="contact-name-input"
                                    />
                                </div>

                                <div>
                                    <Label htmlFor="email">Email *</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                                        placeholder="you@example.com"
                                        className="mt-1"
                                        required
                                        data-testid="contact-email-input"
                                    />
                                </div>

                                <div>
                                    <Label htmlFor="phone">Phone (optional)</Label>
                                    <Input
                                        id="phone"
                                        type="tel"
                                        value={formData.phone}
                                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                                        placeholder="0400 000 000"
                                        className="mt-1"
                                        data-testid="contact-phone-input"
                                    />
                                </div>

                                <div>
                                    <Label htmlFor="message">Message *</Label>
                                    <Textarea
                                        id="message"
                                        value={formData.message}
                                        onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                                        placeholder="How can we help you?"
                                        className="mt-1 min-h-[120px]"
                                        required
                                        data-testid="contact-message-input"
                                    />
                                </div>

                                <Button 
                                    type="submit" 
                                    className="btn-primary w-full py-3 h-auto"
                                    disabled={loading}
                                    data-testid="contact-submit-btn"
                                >
                                    {loading ? (
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : (
                                        <>
                                            <Send className="w-5 h-5 mr-2" />
                                            Send Message
                                        </>
                                    )}
                                </Button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
